import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { heroData } from '../data/mock';
import TextReveal from './TextReveal';
import AnimatedCurve from './AnimatedCurve';
import KairosLogo from './KairosLogo';

/**
 * HeroSection Component
 * 
 * Section hero plein écran avec:
 * - Logo animé au centre (optionnel)
 * - Effet de texte "reveal" sur le titre
 * - Animation de courbe de Bézier en arrière-plan
 * - Parallax subtil au scroll
 * - CTA animé
 */
const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });
  
  // Parallax effect for background elements
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  
  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden px-[5%]"
    >
      {/* Background gradient orbs */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(42, 59, 143, 0.15) 0%, transparent 70%)',
          top: '-200px',
          right: '-200px',
          y: bgY,
          filter: 'blur(60px)',
        }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(42, 59, 143, 0.1) 0%, transparent 70%)',
          bottom: '-100px',
          left: '-100px',
          y: bgY,
          filter: 'blur(40px)',
        }}
      />
      
      {/* Animated Bezier Curve Background */}
      <div className="absolute inset-0 pointer-events-none">
        <AnimatedCurve />
      </div>
      
      {/* Content */}
      <motion.div
        className="max-w-[1440px] mx-auto relative z-10 pt-32 pb-20"
        style={{ opacity }}
      >
        <div className="max-w-4xl">
          {/* Pre-title */}
          <motion.p
            className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Agence de développement sur-mesure
          </motion.p>
          
          {/* Main Title with Reveal Effect */}
          <TextReveal
            text={heroData.tagline}
            className="heading-xl mb-8"
            delay={0.4}
          />
          
          {/* Subtitle */}
          <motion.p
            className="body-lg text-[#888888] mb-12 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            {heroData.subtitle}
          </motion.p>
          
          {/* CTA Button */}
          <motion.a
            href={`mailto:${heroData.email}?subject=Mon moment Kairos`}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A3B8F] text-white font-medium tracking-wide interactive group"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            whileHover={{ 
              backgroundColor: '#4A5BAF',
              scale: 1.02,
            }}
            whileTap={{ scale: 0.98 }}
          >
            {heroData.cta}
            <motion.span
              className="inline-block"
              initial={{ x: 0 }}
              whileHover={{ x: 5 }}
              transition={{ type: 'spring', stiffness: 400 }}
            >
              <ArrowRight size={18} />
            </motion.span>
          </motion.a>
        </div>
      </motion.div>
      
      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <span className="text-[#555555] text-xs uppercase tracking-widest">Découvrir</span>
        <motion.div
          className="w-px h-12 bg-gradient-to-b from-[#2A3B8F] to-transparent"
          animate={{ scaleY: [1, 0.5, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
